import React, {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Badge, Box} from '@mui/material';
import {Icon, designSystemToken} from '@lightricks/react-design-system';
import ProjectShelf from '@contentlab/components/project-shelf';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useContentLabPageName from '@contentlab/hooks/use-content-lab-page-name';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import {setContentLabProjectShelfLastOpenedTimestamp} from '@/utils/LtidStorage';
import queryClient from '@/utils/reactQueryClient';
import {ASSETS, CONTENT_LAB} from '@/hooks/content-studio/consts';

function ProjectShelfButton() {
  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const pageName = useContentLabPageName();

  const isProjectShelfOpen = contentLabStore(
    useShallow((state) => state.isProjectShelfOpen)
  );

  const [badgeCount, setBadgeCount] = useState<number>(0);
  const [projectShelfPresentationId, setProjectShelfPresentationId] =
    useState<string>(uuid());
  const badgeRef = useRef<HTMLDivElement>(null);
  const [projectShelfAnchorEl, setProjectShelfAnchorEl] =
    useState<null | HTMLElement>(null);

  const handleBadgeCountUpdate = useCallback(
    (count: number) => {
      setBadgeCount(count);

      if (count) {
        queryClient.invalidateQueries({queryKey: [CONTENT_LAB, ASSETS]});
      }
    },
    [setBadgeCount]
  );

  const openProjectShelf = (projectShelfInteractionId?: string) => {
    const projectNewShelfPresentationId = uuid();

    setProjectShelfPresentationId(projectNewShelfPresentationId);
    viewPresented({
      presentation_id: projectNewShelfPresentationId,
      interaction_id: projectShelfInteractionId,
      view_name: CONTENT_LAB_VIEW_NAMES.PROJECTS_SHELF,
      view_type: 'popup',
      view_location: pageName,
      source: 'item_pressed',
    });
    setContentLabProjectShelfLastOpenedTimestamp();
    setProjectShelfAnchorEl(badgeRef.current);
  };

  const closeProjectShelf = () => {
    setProjectShelfAnchorEl(null);
    const projectShelfInteractionId = uuid();
    uiInteraction({
      item_name: 'project_shelf_dismissed',
      interaction_location: CONTENT_LAB_VIEW_NAMES.PROJECTS_SHELF,
      interaction_id: projectShelfInteractionId,
    });
    contentLabActions.setIsProjectShelfOpen(false);
    viewDismissed({
      presentation_id: projectShelfPresentationId,
      interaction_id: projectShelfInteractionId,
      reason: 'dismissed',
    });
  };

  const handleProjectShelfClick = () => {
    if (projectShelfAnchorEl) {
      contentLabActions.setIsProjectShelfOpen(false);
      return;
    }
    const projectShelfInteractionId = uuid();
    uiInteraction({
      item_name: 'project_shelf',
      interaction_location: CONTENT_LAB_VIEW_NAMES.HEADER,
      interaction_id: projectShelfInteractionId,
    });
    openProjectShelf(projectShelfInteractionId);
  };

  useEffect(() => {
    if (isProjectShelfOpen) {
      openProjectShelf();
    }
  }, [isProjectShelfOpen]);

  return (
    <Box
      height="32px"
      width="32px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Badge
        ref={badgeRef}
        badgeContent={badgeCount}
        color="primary"
        sx={{
          'cursor': 'pointer',
          '& .MuiBadge-badge': {
            backgroundColor: designSystemToken('semantic.fg.neutral'),
          },
        }}
        onClick={handleProjectShelfClick}
      >
        <Icon name="Navigation-Creations" appearance="primary" size="medium" />
      </Badge>
      {projectShelfAnchorEl && (
        <ProjectShelf
          anchorEl={projectShelfAnchorEl}
          onClose={closeProjectShelf}
          presentationId={projectShelfPresentationId}
          onBadgeCountUpdate={handleBadgeCountUpdate}
        />
      )}
    </Box>
  );
}

export default ProjectShelfButton;
